import React, { useState, useEffect } from "react";
import axios from "axios";
import { Settings as SettingsIcon, Save, ArrowLeft, Globe, Clock, Send, RefreshCw, Key, Code, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState({
    target_api_url: "https://www.crmportal.be/api/invoices/ingest",
    api_method: "POST",
    api_timeout: 30,
    allow_resend: false,
    remote_api_enabled: false,
    remote_api_key: "",
    auto_send_on_success: true
  });
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Ayarlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.target_api_url) {
      toast.error("Hedef API URL'si boş olamaz");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/settings`, settings);
      toast.success("Ayarlar kaydedildi!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!settings.target_api_url) {
      toast.error("Hedef API URL'si boş olamaz");
      return;
    }

    setTesting(true);
    try {
      // Simple connectivity test
      const testData = {
        test: true,
        message: "HYK PDF Test Connection",
        timestamp: new Date().toISOString()
      };

      const response = await axios.post(settings.target_api_url, testData, {
        timeout: settings.api_timeout * 1000
      });

      toast.success(`Bağlantı başarılı! Status: ${response.status}`);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error(`Zaman aşımı (${settings.api_timeout}s)`);
      } else if (error.response) {
        toast.warning(`Yanıt alındı: HTTP ${error.response.status}`);
      } else {
        toast.error(`Bağlantı hatası: ${error.message}`);
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Geri
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
              <SettingsIcon className="w-8 h-8 text-blue-600" />
              Ayarlar
            </h1>
            <p className="text-gray-600 mt-1">API ve sistem ayarlarını yapılandırın</p>
          </div>
        </div>

        {/* API Settings Card */}
        <Card className="glass-card shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Hedef API Ayarları
            </CardTitle>
            <CardDescription>
              PDF verileri gönderilecek harici API yapılandırması
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Target URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Hedef API URL
              </label>
              <Input
                type="url"
                value={settings.target_api_url}
                onChange={(e) => setSettings({ ...settings, target_api_url: e.target.value })}
                placeholder="https://api.example.com/endpoint"
                className="h-12 text-base"
              />
              <p className="text-xs text-gray-500">
                PDF verileri JSON formatında bu adrese gönderilecek
              </p>
            </div>

            {/* HTTP Method */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                HTTP Method
              </label>
              <select
                value={settings.api_method}
                onChange={(e) => setSettings({ ...settings, api_method: e.target.value })}
                className="w-full h-12 px-4 rounded-md border border-gray-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </select>
            </div>

            {/* Timeout */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Zaman Aşımı (saniye)
              </label>
              <Input
                type="number"
                min="5"
                max="120"
                value={settings.api_timeout}
                onChange={(e) => setSettings({ ...settings, api_timeout: parseInt(e.target.value) || 30 })}
                className="h-12 text-base"
              />
              <p className="text-xs text-gray-500">
                API yanıt bekleme süresi (5-120 saniye)
              </p>
            </div>

            {/* Allow Resend Toggle */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-800">Tekrar Gönder Özelliği</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Aktif olduğunda, gönderilmiş içerikler tekrar gönderilebilir
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, allow_resend: !settings.allow_resend })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.allow_resend ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.allow_resend ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {settings.allow_resend && (
                <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-700">
                    ✓ "Gönderildi" durumundaki içeriklerde "Tekrar Gönder" butonu görünecek
                  </p>
                </div>
              )}
            </div>

            {/* Data Preview */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Gönderilecek Veri Formatı:</p>
              <pre className="text-xs text-gray-600 overflow-x-auto">
{`{
  "invoice_number": "DIS2025011789",
  "invoice_date": "21-01-2026",
  "company_name": "AYTU",
  "week_number": 4,
  "total_amount": "37.259,80",
  "employees": [
    {
      "name": "Hasan, A.",
      "date": "16-01-2026",
      "quantity": 85,
      "unit_price": 1.26,
      "fixed_charge": 0.80,
      "total": 107.90
    },
    ...
  ]
}`}
              </pre>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="flex-1 h-12"
              >
                {testing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Test Ediliyor...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Bağlantıyı Test Et
                  </span>
                )}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Kaydediliyor...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Kaydet
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Remote API Settings Card */}
        <Card className="glass-card mt-6 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-600" />
              Uzak API Ayarları
            </CardTitle>
            <CardDescription>
              Harici sistemlerden PDF yüklemek için API yapılandırması
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Remote API */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-800">Uzak API'yi Etkinleştir</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Harici sistemlerin PDF yüklemesine izin ver
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, remote_api_enabled: !settings.remote_api_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.remote_api_enabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.remote_api_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {settings.remote_api_enabled && (
              <>
                {/* API Key */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    API Anahtarı
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={settings.remote_api_key}
                      onChange={(e) => setSettings({ ...settings, remote_api_key: e.target.value })}
                      placeholder="Güvenli bir API anahtarı girin"
                      className="h-12 text-base font-mono"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const key = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
                        setSettings({ ...settings, remote_api_key: key });
                        toast.success("Yeni API anahtarı oluşturuldu");
                      }}
                    >
                      Oluştur
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Bu anahtar uzak API isteklerinde kimlik doğrulama için kullanılır
                  </p>
                </div>

                {/* Auto Send on Success */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Send className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-800">Başarılı Yüklemeleri Otomatik Gönder</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Tutar uyuşmazlığı yoksa hedef sunucuya otomatik gönder
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, auto_send_on_success: !settings.auto_send_on_success })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.auto_send_on_success ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.auto_send_on_success ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* API Documentation */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    API Dokümantasyonu
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-purple-700">Endpoint:</p>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs bg-white px-3 py-2 rounded border border-purple-200 flex-1 overflow-x-auto">
                          POST {window.location.origin}/api/remote/upload
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/remote/upload`);
                            setCopied("endpoint");
                            setTimeout(() => setCopied(""), 2000);
                          }}
                          className="p-2 hover:bg-purple-100 rounded"
                        >
                          {copied === "endpoint" ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-purple-600" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-purple-700">Parametreler (form-data):</p>
                      <div className="mt-2 text-xs space-y-1">
                        <div className="flex gap-2">
                          <code className="bg-white px-2 py-1 rounded text-purple-800 font-semibold">file</code>
                          <span className="text-purple-600">PDF dosyası (zorunlu)</span>
                        </div>
                        <div className="flex gap-2">
                          <code className="bg-white px-2 py-1 rounded text-purple-800 font-semibold">api_key</code>
                          <span className="text-purple-600">API anahtarı (zorunlu)</span>
                        </div>
                        <div className="flex gap-2">
                          <code className="bg-white px-2 py-1 rounded text-purple-800 font-semibold">duplicate_mode</code>
                          <span className="text-purple-600">"skip" | "add" | "ask" (varsayılan: skip)</span>
                        </div>
                        <div className="flex gap-2">
                          <code className="bg-white px-2 py-1 rounded text-purple-800 font-semibold">auto_send</code>
                          <span className="text-purple-600">true | false (opsiyonel)</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-purple-700">Örnek cURL:</p>
                      <div className="relative mt-1">
                        <pre className="text-xs bg-gray-800 text-green-400 p-3 rounded overflow-x-auto">
{`curl -X POST "${window.location.origin}/api/remote/upload" \\
  -F "file=@fatura.pdf" \\
  -F "api_key=${settings.remote_api_key || 'YOUR_API_KEY'}" \\
  -F "duplicate_mode=skip" \\
  -F "auto_send=true"`}
                        </pre>
                        <button
                          onClick={() => {
                            const cmd = `curl -X POST "${window.location.origin}/api/remote/upload" -F "file=@fatura.pdf" -F "api_key=${settings.remote_api_key || 'YOUR_API_KEY'}" -F "duplicate_mode=skip" -F "auto_send=true"`;
                            navigator.clipboard.writeText(cmd);
                            setCopied("curl");
                            setTimeout(() => setCopied(""), 2000);
                          }}
                          className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                        >
                          {copied === "curl" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-400" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-purple-700">duplicate_mode Açıklaması:</p>
                      <div className="mt-2 text-xs space-y-1 text-purple-600">
                        <p><code className="bg-white px-1 rounded">skip</code> - Aynı fatura varsa yükleme yapma</p>
                        <p><code className="bg-white px-1 rounded">add</code> - Aynı fatura olsa bile yükle</p>
                        <p><code className="bg-white px-1 rounded">ask</code> - Mükerrer bilgisini döndür, yükleme yapma</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-purple-700">Başarılı Yanıt:</p>
                      <pre className="text-xs bg-white p-2 rounded border border-purple-200 overflow-x-auto mt-1">
{`{
  "success": true,
  "upload_id": "abc-123",
  "invoice_number": "DIS2025011789",
  "sent_to_server": true,
  "validation_warning": false
}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="glass-card mt-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <SettingsIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Bilgi</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Bu ayarlar "Gönder" butonuna tıkladığınızda kullanılır. PDF verileri JSON formatında 
                  belirttiğiniz API adresine gönderilir. Değişiklikler anında uygulanır.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
